import { useCallback, useEffect, useState } from "react";
import type { DependencyList } from "react";
import { getApiErrorMessage } from "../utils";

interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

interface UseAsyncOptions {
  errorMessage?: string;
}

export interface UseAsyncResult<T> extends AsyncState<T> {
  reload: () => void;
}

export function useAsync<T>(
  asyncFn: () => Promise<T>,
  deps: DependencyList,
  options: UseAsyncOptions = {},
): UseAsyncResult<T> {
  const { errorMessage = "Something went wrong." } = options;
  const [state, setState] = useState<AsyncState<T>>({
    data: null,
    loading: true,
    error: null,
  });
  const [reloadIndex, setReloadIndex] = useState(0);

  const reload = useCallback(() => setReloadIndex((index) => index + 1), []);

  useEffect(() => {
    let cancelled = false;
    const timeoutId = window.setTimeout(() => {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      asyncFn()
        .then((result) => {
          if (!cancelled) setState({ data: result, loading: false, error: null });
        })
        .catch((caughtError) => {
          if (!cancelled) {
            setState((prev) => ({
              ...prev,
              loading: false,
              error: getApiErrorMessage(caughtError, errorMessage),
            }));
          }
        });
    }, 0);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, reloadIndex]);

  return { ...state, reload };
}
